import React from "react";
import {
  ArrowDown,
  Check,
  ChevronDown,
  CodeXml,
  Copy,
  Headphones,
  Link2,
  PackageCheck,
  RefreshCw,
  Send,
  ShoppingBag,
  Store,
  Truck,
  Wallet,
} from "lucide-react";

import Reveal from "../redesign/Reveal";

/** "More than escrow" — three capability stories with realistic dashboard mockups. */
const ProductShowcase: React.FC = () => {
  return (
    <section className='product-showcase' id='features'>
      <div className='section-header section-header--start section-header--light product-showcase__header'>
        <p className='eyebrow'>MORE THAN ESCROW</p>
        <h2>Everything your business needs to complete the deal</h2>
        <p className='section-header__description'>
          Manage, sell, deliver and grow from one protected transaction platform.
        </p>
      </div>

      <div className='product-showcase__stories'>
        {/* Story 1 — Stay in control */}
        <Reveal as='article' className='product-showcase__story'>
          <div className='product-showcase__story-copy'>
            <p className='product-showcase__index'>Stay in control</p>
            <div className='product-showcase__capability'>
              <h3>Control every transaction</h3>
              <p className='product-showcase__copy'>
                See every order, payment state, delivery update and notification from one calm
                dashboard.
              </p>
            </div>
            <div className='product-showcase__capability'>
              <h3>Buy and sell from one account</h3>
              <p className='product-showcase__copy'>
                Switch roles when the deal changes—without another login, profile or fragmented
                history.
              </p>
            </div>
          </div>
          <div
            aria-hidden='true'
            className='product-showcase__environment product-showcase__environment--transactions'>
            <aside className='product-showcase__sidebar'>
              <span className='product-showcase__product-logo'>V</span>
              <span data-active='true'>
                <ShoppingBag size={16} /> Transactions
              </span>
              <span>
                <Store size={16} /> Catalog
              </span>
              <span>
                <Link2 size={16} /> Quick Links
              </span>
              <span>
                <Wallet size={16} /> Wallet
              </span>
            </aside>
            <div className='product-showcase__product-main'>
              <div className='product-showcase__product-header'>
                <div>
                  <small>Good afternoon, Tola</small>
                  <strong>Transactions</strong>
                </div>
                <div className='product-showcase__role-switch'>
                  <span>Buyer</span>
                  <span data-active='true'>Vendor</span>
                </div>
              </div>
              <div className='product-showcase__metrics'>
                <div>
                  <small>Protected volume</small>
                  <strong>₦2.48m</strong>
                  <em>+18.4%</em>
                </div>
                <div>
                  <small>Active orders</small>
                  <strong>24</strong>
                  <em>6 need attention</em>
                </div>
                <div>
                  <small>Available balance</small>
                  <strong>₦384k</strong>
                  <em>Withdraw</em>
                </div>
              </div>
              <div className='product-showcase__table'>
                <div className='product-showcase__table-head'>
                  <span>Customer</span>
                  <span>Status</span>
                  <span>Amount</span>
                </div>
                {[
                  { i: "A", name: "Ayo Stores", tone: "green", status: "Delivery confirmed", amt: "₦85,000" },
                  { i: "M", name: "Mina Studio", tone: "blue", status: "In delivery", amt: "₦240,000" },
                  { i: "T", name: "Tola Home", tone: "blue", status: "Payment secured", amt: "₦62,500" },
                  { i: "N", name: "Nora Living", tone: "yellow", status: "Needs confirmation", amt: "₦118,000" },
                ].map((row) => (
                  <div className='product-showcase__table-row' key={row.name}>
                    <span>
                      <i>{row.i}</i>
                      <b>{row.name}</b>
                    </span>
                    <span className='product-showcase__status' data-tone={row.tone}>
                      {row.status}
                    </span>
                    <strong>{row.amt}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Story 2 — Convert demand */}
        <Reveal as='article' className='product-showcase__story product-showcase__story--reverse'>
          <div className='product-showcase__story-copy'>
            <p className='product-showcase__index'>Convert demand</p>
            <div className='product-showcase__capability'>
              <h3>Sell anywhere</h3>
              <p className='product-showcase__copy'>
                Share Quick Links, open a verified catalog, or let Vendy complete the order on
                WhatsApp.
              </p>
            </div>
            <div className='product-showcase__capability'>
              <h3>Keep money moving safely</h3>
              <p className='product-showcase__copy'>
                Request a partial release for fulfilment costs, then withdraw available funds to
                your bank.
              </p>
            </div>
          </div>
          <div
            aria-hidden='true'
            className='product-showcase__environment product-showcase__environment--commerce'>
            <div className='product-showcase__commerce-header'>
              <div>
                <small>Catalog</small>
                <strong>Tola Store</strong>
              </div>
              <span className='product-showcase__mock-button'>
                <Link2 size={15} /> Create Quick Link
              </span>
            </div>
            <div className='product-showcase__commerce-body'>
              <div className='product-showcase__catalog-panel'>
                <div className='product-showcase__catalog-toolbar'>
                  <span>Products · 18</span>
                  <ChevronDown size={15} />
                </div>
                <div className='product-showcase__products'>
                  <div>
                    <span className='product-showcase__product-art product-showcase__product-art--bag'>
                      ◒
                    </span>
                    <strong>Leather travel bag</strong>
                    <small>₦85,000</small>
                  </div>
                  <div>
                    <span className='product-showcase__product-art product-showcase__product-art--shoe'>
                      ◓
                    </span>
                    <strong>Everyday runners</strong>
                    <small>₦42,500</small>
                  </div>
                </div>
              </div>
              <div className='product-showcase__link-panel'>
                <div className='product-showcase__link-top'>
                  <span className='product-showcase__link-icon'>
                    <Link2 size={16} />
                  </span>
                  <span className='product-showcase__status' data-tone='green'>
                    Live
                  </span>
                </div>
                <small>Fixed Quick Link</small>
                <strong>Leather travel bag</strong>
                <p>Collect exactly ₦85,000 through one protected order link.</p>
                <div className='product-showcase__copy-field'>
                  <span>vendstash.com/pay/tolastore</span>
                  <Copy size={14} />
                </div>
                <span className='product-showcase__mock-button product-showcase__mock-button--wide'>
                  <Send size={14} /> Share link
                </span>
              </div>
            </div>
            <div className='product-showcase__wallet-bar'>
              <span>
                <Wallet size={17} />
                <span>
                  <small>Available balance</small>
                  <strong>₦384,250</strong>
                </span>
              </span>
              <span className='product-showcase__mock-button product-showcase__mock-button--light'>
                <ArrowDown size={14} /> Withdraw
              </span>
              <span className='product-showcase__mock-button product-showcase__mock-button--light'>
                <RefreshCw size={14} /> Partial release
              </span>
            </div>
          </div>
        </Reveal>

        {/* Story 3 — Fulfil and grow */}
        <Reveal as='article' className='product-showcase__story'>
          <div className='product-showcase__story-copy'>
            <p className='product-showcase__index'>Fulfil and grow</p>
            <div className='product-showcase__capability'>
              <h3>Complete orders with confidence</h3>
              <p className='product-showcase__copy'>
                Coordinate logistics, follow delivery progress and resolve issues with evidence and
                human support.
              </p>
            </div>
            <div className='product-showcase__capability'>
              <h3>Grow with VendStash</h3>
              <p className='product-showcase__copy'>
                Connect secure transaction infrastructure through the Business API and earn through
                referrals.
              </p>
            </div>
          </div>
          <div
            aria-hidden='true'
            className='product-showcase__environment product-showcase__environment--operations'>
            <div className='product-showcase__operations-header'>
              <div>
                <small>Order #VS-2841</small>
                <strong>Delivery and resolution</strong>
              </div>
              <span className='product-showcase__status' data-tone='blue'>
                In progress
              </span>
            </div>
            <div className='product-showcase__operations-grid'>
              <div className='product-showcase__timeline-panel'>
                <div className='product-showcase__delivery-map'>
                  <span>
                    <Store size={15} />
                  </span>
                  <i />
                  <span data-active='true'>
                    <Truck size={17} />
                  </span>
                  <i />
                  <span>
                    <PackageCheck size={16} />
                  </span>
                </div>
                <strong>Package is out for delivery</strong>
                <small>ETA today, 3:40 PM · Kwik Logistics</small>
                <div className='product-showcase__timeline-steps'>
                  <span data-done='true'>Payment secured</span>
                  <span data-done='true'>Vendor dispatched</span>
                  <span>Buyer confirmation</span>
                </div>
              </div>
              <div className='product-showcase__case-panel'>
                <span className='product-showcase__case-label'>
                  <Headphones size={17} /> Mediation centre
                </span>
                <strong>Case evidence received</strong>
                <p>Both parties can review the record before a support specialist responds.</p>
                <div className='product-showcase__evidence-row'>
                  <span>Invoice.pdf</span>
                  <Check size={15} />
                </div>
                <div className='product-showcase__support-row'>
                  <span className='product-showcase__support-avatar'>AO</span>
                  <span>
                    <strong>Amaka · Support</strong>
                    <small>Reviewing the case</small>
                  </span>
                </div>
              </div>
            </div>
            <div className='product-showcase__integration-bar'>
              <span>
                <CodeXml size={16} />
                <span>
                  <small>Business API</small>
                  <strong>12,480 successful requests</strong>
                </span>
              </span>
              <span>
                <RefreshCw size={16} />
                <span>
                  <small>Referral rewards</small>
                  <strong>₦24,500 earned</strong>
                </span>
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ProductShowcase;
